import React, { useState, useMemo, useEffect, useRef } from "react";
import Modal from "react-bootstrap/Modal";
import { Button } from "react-bootstrap";
import { toast } from "react-toastify";
import _ from "lodash";
import { CheckCircle, Archive, Clock, X, ArrowLeft } from "lucide-react";
import * as apiService from "../../services/v2/apiService";
import * as endPoints from "../../constants/v2/endPoints";
import {
  preloadPlaces,
  getAutocompletePredictions,
} from "../../services/placesService";
import { getGeoLocation } from "../../services/commonServices";
import { thousandSeperatedValue } from "../../helpers/v2/common";
import "./WantAdStatusModal.scss";

const ARCHIVE_REASONS = [
  { id: "paused", label: "Client paused / no longer looking" },
  { id: "found_elsewhere", label: "Client found through a different agent" },
  { id: "switched_criteria", label: "Switched price range or area" },
  { id: "timing", label: "Timing didn't work out" },
  { id: "other", label: "Other" },
];

const WantAdStatusModal = ({ show, onHide, wantAdId, status, title, onSaved }) => {
  const targetStatus = (status || "").toUpperCase();
  const [submitting, setSubmitting] = useState(false);

  const [propertyAddress, setPropertyAddress] = useState("");
  const [propertyLat, setPropertyLat] = useState(null);
  const [propertyLng, setPropertyLng] = useState(null);
  const [propertyZip, setPropertyZip] = useState("");
  const [addressPredictions, setAddressPredictions] = useState([]);
  const [showAddressDropdown, setShowAddressDropdown] = useState(false);
  const [closedDate, setClosedDate] = useState("");
  const [purchasePrice, setPurchasePrice] = useState("");
  const [archiveReason, setArchiveReason] = useState("");
  const [archiveNotes, setArchiveNotes] = useState("");
  const [expectedCloseDate, setExpectedCloseDate] = useState("");

  const blurTimeoutRef = useRef(null);

  useEffect(() => {
    if (show && targetStatus === "CLOSED") {
      preloadPlaces();
    }
  }, [show, targetStatus]);

  const reset = () => {
    setPropertyAddress("");
    setPropertyLat(null);
    setPropertyLng(null);
    setPropertyZip("");
    setAddressPredictions([]);
    setShowAddressDropdown(false);
    setClosedDate("");
    setPurchasePrice("");
    setArchiveReason("");
    setArchiveNotes("");
    setExpectedCloseDate("");
    setSubmitting(false);
  };

  const handleAddressChange = async (value) => {
    setPropertyAddress(value);
    // User edited the field - selected place is no longer authoritative
    setPropertyLat(null);
    setPropertyLng(null);
    setPropertyZip("");

    const trimmed = value.trim();
    if (trimmed.length < 3) {
      setAddressPredictions([]);
      return;
    }
    const predictions = await getAutocompletePredictions(trimmed, {
      types: ["address"],
      componentRestrictions: { country: "us" },
    });
    setAddressPredictions(predictions.slice(0, 5));
  };

  const handleAddressSelect = async (prediction) => {
    const description = prediction.description;
    const reqPayload = `address=${encodeURIComponent(description)}&components=country:US`;
    try {
      const geoData = await getGeoLocation(reqPayload);
      const result = _.get(geoData, "results.0", null);
      if (result) {
        const formatted = _.get(result, "formatted_address", description);
        const lat = _.get(result, "geometry.location.lat", null);
        const lng = _.get(result, "geometry.location.lng", null);
        const components = _.get(result, "address_components", []);
        const zipComp = components.find((c) =>
          (c.types || []).includes("postal_code")
        );
        const zip = zipComp ? _.get(zipComp, "short_name", "") : "";
        setPropertyAddress(formatted);
        setPropertyLat(lat);
        setPropertyLng(lng);
        setPropertyZip(zip);
      } else {
        setPropertyAddress(description);
      }
    } catch (e) {
      setPropertyAddress(description);
    }
    setAddressPredictions([]);
    setShowAddressDropdown(false);
  };

  const handleAddressBlur = () => {
    blurTimeoutRef.current = setTimeout(() => {
      setShowAddressDropdown(false);
    }, 200);
  };

  const handleAddressFocus = () => {
    if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    setShowAddressDropdown(true);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onHide && onHide();
  };

  const headerCfg = useMemo(() => {
    if (targetStatus === "CLOSED")
      return {
        Icon: CheckCircle,
        accent: "var(--accent-d-500, #16a34a)",
        title: "Close out this Want-Listing",
        helper: null,
      };
    if (targetStatus === "PENDING")
      return {
        Icon: Clock,
        accent: "var(--primary-500, #1f3a8a)",
        title: "Mark this Want-Listing pending",
        helper:
          "Hides this from the public feed and pauses new matches. You can re-activate any time.",
      };
    if (targetStatus === "ARCHIVED")
      return {
        Icon: Archive,
        accent: "var(--neutral-700, #475569)",
        title: "Archive this Want-Listing",
        helper:
          "Removes it from the feed. Optional: tell us why so we can improve.",
      };
    if (targetStatus === "ACTIVE")
      return {
        Icon: Clock,
        accent: "var(--primary-500, #1f3a8a)",
        title: "Re-activate this Want-Listing",
        helper:
          "Puts this back on the public feed and re-enables matching.",
      };
    return null;
  }, [targetStatus]);

  const cleanedPrice = String(purchasePrice).replace(/[$,\s]/g, "");
  const priceNum = parseFloat(cleanedPrice);
  const validClosed =
    targetStatus !== "CLOSED" ||
    (propertyAddress.trim().length >= 5 &&
      cleanedPrice.length > 0 &&
      !Number.isNaN(priceNum) &&
      priceNum > 0);
  const canSubmit = !submitting && validClosed && !!targetStatus;

  const submit = async () => {
    setSubmitting(true);
    try {
      const outcome = {};
      if (targetStatus === "CLOSED") {
        outcome.didPurchase = true;
        outcome.propertyAddress = propertyAddress.trim();
        if (propertyLat != null) outcome.propertyLat = propertyLat;
        if (propertyLng != null) outcome.propertyLng = propertyLng;
        if (propertyZip) outcome.propertyZip = propertyZip;
        if (closedDate) outcome.closedDate = closedDate;
        if (purchasePrice) {
          outcome.purchasePrice = parseFloat(
            String(purchasePrice).replace(/[$,]/g, "")
          );
        }
      } else if (targetStatus === "ARCHIVED") {
        if (archiveReason) outcome.archiveReason = archiveReason;
        if (archiveNotes.trim()) outcome.archiveNotes = archiveNotes.trim();
      } else if (targetStatus === "PENDING") {
        if (expectedCloseDate) outcome.expectedCloseDate = expectedCloseDate;
      }

      await apiService.apiCall(
        endPoints.SET_WANTAD_CLOSEOUT_STATUS.replace(":wantAdId", wantAdId),
        "POST",
        { status: targetStatus, outcome }
      );

      const successMsg =
        targetStatus === "CLOSED"
          ? "Want-Listing closed. Nice work."
          : targetStatus === "PENDING"
          ? "Marked pending."
          : targetStatus === "ARCHIVED"
          ? "Listing archived."
          : "Listing re-activated.";
      toast.success(successMsg);
      reset();
      onSaved && onSaved(targetStatus);
      onHide && onHide();
    } catch (err) {
      toast.error(err?.message || "Could not update status. Try again.");
      setSubmitting(false);
    }
  };

  if (!headerCfg) return null;
  const Icon = headerCfg.Icon;

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="md"
      backdrop={submitting ? "static" : true}
      keyboard={!submitting}
      dialogClassName="wantad-status-modal"
    >
      <Modal.Body>
        <div className="status-modal-header">
          <div
            className="status-modal-icon"
            style={{ background: headerCfg.accent }}
          >
            <Icon size={20} strokeWidth={2} color="#fff" />
          </div>
          <button
            type="button"
            className="status-modal-close"
            onClick={handleClose}
            aria-label="Close"
          >
            <X size={20} strokeWidth={1.8} />
          </button>
        </div>

        <h3 className="status-modal-title">{headerCfg.title}</h3>
        {title && <div className="status-modal-listing">{title}</div>}
        {headerCfg.helper && (
          <p className="status-modal-helper">{headerCfg.helper}</p>
        )}

        {targetStatus === "CLOSED" && (
          <div className="status-form">
            <div className="field">
              <label>Property the client purchased</label>
              <div className="address-autocomplete">
                <input
                  type="text"
                  placeholder="123 N Hayden Rd, Scottsdale, AZ 85251"
                  value={propertyAddress}
                  onChange={(e) => handleAddressChange(e.target.value)}
                  onFocus={handleAddressFocus}
                  onBlur={handleAddressBlur}
                  autoComplete="off"
                />
                {showAddressDropdown && addressPredictions.length > 0 && (
                  <ul className="address-autocomplete__list">
                    {addressPredictions.map((p) => (
                      <li
                        key={p.place_id}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleAddressSelect(p)}
                      >
                        {p.description}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <div className="field">
              <label>Purchase price</label>
              <div className="price-input">
                <span className="price-input__symbol">$</span>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="1,250,000"
                  value={thousandSeperatedValue(purchasePrice) || ""}
                  onChange={(e) =>
                    setPurchasePrice(e.target.value.replace(/[^0-9]/g, ""))
                  }
                />
              </div>
            </div>
            <div className="field">
              <label>Closing date <span className="optional">optional</span></label>
              <input
                type="date"
                value={closedDate}
                onChange={(e) => setClosedDate(e.target.value)}
              />
            </div>
          </div>
        )}

        {targetStatus === "PENDING" && (
          <div className="status-form">
            <div className="field">
              <label>Expected close date <span className="optional">(optional)</span></label>
              <input
                type="date"
                value={expectedCloseDate}
                onChange={(e) => setExpectedCloseDate(e.target.value)}
              />
            </div>
          </div>
        )}

        {targetStatus === "ARCHIVED" && (
          <div className="status-form">
            <div className="field">
              <label>Why are you archiving? <span className="optional">(optional)</span></label>
              <div className="reason-list">
                {ARCHIVE_REASONS.map((r) => (
                  <label key={r.id} className="reason-row">
                    <input
                      type="radio"
                      name="archiveReason"
                      value={r.id}
                      checked={archiveReason === r.id}
                      onChange={() => setArchiveReason(r.id)}
                    />
                    <span>{r.label}</span>
                  </label>
                ))}
              </div>
            </div>
            {archiveReason === "other" && (
              <div className="field">
                <label>Tell us more</label>
                <textarea
                  rows={3}
                  maxLength={500}
                  value={archiveNotes}
                  onChange={(e) => setArchiveNotes(e.target.value)}
                />
              </div>
            )}
          </div>
        )}

        <div className="status-actions">
          <Button variant="link" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!canSubmit}
            onClick={submit}
          >
            {submitting
              ? "Saving..."
              : targetStatus === "CLOSED"
              ? "Save & close listing"
              : targetStatus === "PENDING"
              ? "Mark pending"
              : targetStatus === "ARCHIVED"
              ? "Archive listing"
              : "Re-activate"}
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default WantAdStatusModal;
